import type { ReactNode, RefObject } from 'react';
import type { APIComponentAttributes, ArdoqId } from '@ardoq/api-types';
import { COMPONENT_ID_ATTRIBUTE } from '@ardoq/global-consts';
import styled from 'styled-components';
import { MarkdownViewer } from '@ardoq/markdown';
import { BulkHoverPopover } from '@ardoq/popovers';
import { connect } from '@ardoq/rxbeach';
import { map } from 'rxjs/operators';
import {
  type ComponentsState,
  components$,
} from 'streams/components/components$';

const PopoverContainer = styled.div`
  max-width: 240px;
  max-height: 240px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

type ComponentsById = Partial<Record<ArdoqId, APIComponentAttributes>>;

export const resolveComponentDescriptionPopover = (
  element: HTMLElement | SVGElement,
  componentsById: ComponentsById
): ReactNode | null => {
  const componentId = element.getAttribute(COMPONENT_ID_ATTRIBUTE);
  if (!componentId) {
    return null;
  }
  const description = componentsById[componentId]?.description;
  if (!description) {
    return null;
  }
  return (
    <PopoverContainer>
      <MarkdownViewer content={description} sanitationLevel="permissive" />
    </PopoverContainer>
  );
};

type ComponentDescriptionPopoverProps = {
  rootRef: RefObject<HTMLElement | null>;
  componentsById: ComponentsById;
};

const ComponentDescriptionPopover = ({
  rootRef,
  componentsById,
}: ComponentDescriptionPopoverProps): ReactNode => (
  <BulkHoverPopover
    rootRef={rootRef}
    resolveContent={element =>
      resolveComponentDescriptionPopover(element, componentsById)
    }
  />
);

const toComponentsById = ({
  byId,
}: ComponentsState): Pick<
  ComponentDescriptionPopoverProps,
  'componentsById'
> => ({
  componentsById: byId,
});

export const ConnectedComponentDescriptionPopover = connect(
  ComponentDescriptionPopover,
  components$.pipe(map(toComponentsById))
);
